import { DoubleState, useDoubleStore } from '@/context/DoubleProvider'
import { Box, Button, Flex, Icon, List, SimpleGrid, Text, useMediaQuery } from '@chakra-ui/react'
import { useState } from 'react'
import { ROULETTE_CONTRACT_ADDRESS } from '@/constants/constants'
import { useAudio, SOUNDS } from '@/context/AudioProvider'
import { useSession } from '@/context/SessionProvider'
import { getDoubleWinningColor } from '@/helpers/getDoubleWinningColor'
import { AiOutlineUser } from 'react-icons/ai'
import { AnimatedAmount } from '../../../atoms/AnimatedAmount/AnimatedAmount'
import { DoubleField } from '../atoms/DoubleField'
import { BigNumberInWei } from '@injectivelabs/utils'
import posthog from 'posthog-js'
import { UserBet } from '../atoms/UserBet'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { getTokenByDenom } from '@/services/token-service'
import { useMutation } from '@tanstack/react-query'
import { MobileDoubleBets } from '../molecules/MobileDoubleBets'
import { DoubleColor } from '@/__generated__/graphql'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { useShallow } from 'zustand/react/shallow'
import { placeBetTransaction } from '@/services/GameBroadcaster'
import { useAppToast } from '@/hooks/useAppToast'
import { useTranslation } from 'next-i18next/pages'

export const DoubleBets = () => {
  const colors = [DoubleColor.Black, DoubleColor.Green, DoubleColor.Red]

  return (
    <>
      <SimpleGrid columns={{ base: 2, md: 3 }} gap={2} mb={{ base: 4, md: 9 }} alignItems={'self-start'}>
        {colors.map((color) => (
          <Box
            key={color}
            order={{ base: color === DoubleColor.Green ? 2 : 1, md: 1 }}
            gridColumnStart={color === DoubleColor.Green ? { base: 1, md: 2 } : 'auto'}
            gridColumnEnd={color === DoubleColor.Green ? { base: 3, md: 2 } : 'auto'}
          >
            <DoubleBetsColumn type={color} />
          </Box>
        ))}
      </SimpleGrid>
      <MobileDoubleBets colors={colors} />
    </>
  )
}

const DoubleBetsColumn = ({ type }: { type: DoubleColor }) => {
  const multiplier = type === DoubleColor.Green ? 14 : 2

  return (
    <Box bg={{ base: 'none', md: 'ninja.600' }} rounded={'3xl'} p={{ base: 0, md: 3 }}>
      <BetsButton type={type} multiplier={multiplier} />
      <BetsCounter multiplier={multiplier} type={type} />
      {/* <PlayerBet type={type} /> */}
      <BetsList type={type} />
    </Box>
  )
}

const BetsButton = ({ type, multiplier }: { type: DoubleColor; multiplier: number }) => {
  const { t } = useTranslation()
  const isRoundFinished = useDoubleStore((state) => state.isRoundFinished)
  const currentRoll = useDoubleStore((state) => state.currentRoll)
  const amount = useDoubleStore((state) => state.amount)
  const denom = useDoubleStore((state) => state.denom)
  const errors = useDoubleStore((state) => state.errors)
  const isDirty = useDoubleStore((state) => state.isDirty)
  const isAllowedToBet = useDoubleStore(
    useShallow((state) => [DoubleState.INIT, DoubleState.PROCESSING].includes(state.state!))
  )

  const { account, signIn } = useSession()
  const [isDesktop] = useMediaQuery('(min-width: 768px)')
  const [isLoading, setIsLoading] = useState(false)
  const audio = useAudio()

  const isHighlighted = getDoubleWinningColor(currentRoll) === type && isRoundFinished
  const { onSuccess } = useAppToast()
  const { mutateAsync: handleBet } = useMutation({
    mutationFn: async (color: DoubleColor) => {
      if (!account) {
        signIn()
        return
      }

      setIsLoading(true)

      await placeBetTransaction({
        contract_addr: ROULETTE_CONTRACT_ADDRESS,
        funds: {
          denom: denom,
          amount: amount,
        },
        msg: {
          bet: color.toLowerCase(),
        },
      })
        .then(() => {
          audio.play(SOUNDS.PLAYER_BET)
          onSuccess()
          posthog.capture('user_placed_bet', { game: 'double' })
        })
        .catch(() => {
          audio.play(SOUNDS.PLAYER_BET_ERROR)
          posthog.capture('user_placed_bet_error', { game: 'double' })
        })

      setIsLoading(false)
    },
  })

  return (
    <Flex gap={1} mb={{ base: 0, md: 3 }}>
      <Box flexShrink={0}>
        <DoubleField
          isHighlighted={isDesktop && isHighlighted}
          isDisabled={!isAllowedToBet || isLoading}
          type={type}
          boxSize={isDesktop ? 16 : 12}
        />
      </Box>
      <Button
        onClick={() => handleBet(type)}
        isLoading={isLoading}
        isDisabled={!isAllowedToBet || (!!errors.length && isDirty)}
        bgGradient={buttonsColors[type]}
        _hover={{
          bgGradient: buttonsHoverColors[type],
          boxShadow: buttonsHoverShadow[type],
        }}
        _active={{
          bgGradient: buttonsHoverColors[type],
        }}
        border={'none'}
        w={'full'}
        h={{ base: 12, md: 16 }}
        rounded={'2xl'}
        px={{ base: '4', xl: '6' }}
      >
        <Flex
          w={'full'}
          flexDir={{ base: 'column', sm: 'row', md: 'column', xl: 'row' }}
          justify={'space-between'}
          alignItems={{ base: 'start', sm: 'center', md: 'start', xl: 'center' }}
        >
          <Text>{t('common.place-a-bet')}</Text>
          <Text
            fontSize={{ base: 'xs', sm: 'sm', md: 'xs', xl: 'sm' }}
            display={{ base: 'none', md: 'block' }}
            fontWeight={700}
            bg={'whiteAlpha.200'}
            py={1}
            px={2.5}
            rounded={'lg'}
          >
            {t('double.win-multiplier', { multiplier })}
          </Text>
        </Flex>
      </Button>
    </Flex>
  )
}

const BetsCounter = ({ type, multiplier }: { type: DoubleColor; multiplier: number }) => {
  const bets = useStoreWithEqualityFn(
    useDoubleStore,
    (state) => state.bets[type],
    (oldBets, newBets) => JSON.stringify(oldBets) == JSON.stringify(newBets)
  )

  const denom = useDoubleStore((state) => state.denom)
  const maxPool = useDoubleStore((state) => state.maxPool)

  const isFinished = useDoubleStore((state) => state.isRoundFinished && state.state == DoubleState.FINISHED)
  const isWinningColor = getDoubleWinningColor(useDoubleStore.getState().currentRoll) === type

  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const formatBalance = useFiatCurrencyStore((s) => s.formatBalance)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)
  const isUsdc = getTokenByDenom(denom)?.currency === SupportedCurrencies.USDC

  const betsSum =
    bets
      .filter((bet) => bet.denom.replaceAll('_', '/') === denom)
      .reduce((prev, curr) => prev.plus(curr.bet), new BigNumberInWei(0))
      .toString() || '0'

  const pool = new TokenMoney(betsSum, denom).humanBigNumber
  const poolValue = isFinished && isWinningColor ? pool.multipliedBy(multiplier).toNumber() : pool.toNumber()
  const displayValue = isUsdc && !isUsd ? usdcToFiat(poolValue) : poolValue

  const maxPoolHuman = TokenMoney.fromHuman(maxPool, denom).humanNumber
  const maxPoolDisplay = isUsdc && !isUsd ? formatBalance(maxPoolHuman) : TokenMoney.fromHuman(maxPool, denom).humanFormat

  return (
    <Flex
      display={{ base: 'none', md: 'flex' }}
      mb={{ base: 0, md: 3 }}
      _last={{
        mb: 0,
      }}
      bg={'ninja.500'}
      rounded={'xl'}
      p={3}
      justifyContent={'space-between'}
    >
      <Flex display={{ md: 'none', lg: 'flex' }} alignItems={'center'} gap={0.5}>
        <Icon as={AiOutlineUser} boxSize={4} />
        <Text fontWeight={700} fontSize={'sm'} lineHeight={1}>
          {bets.length}
        </Text>
      </Flex>
      <Flex gap={1}>
        <AnimatedAmount
          minWidth="35px"
          suffix={isUsdc && !isUsd ? getSymbol() : undefined}
          amountProps={{
            currencyStyles: {
              display: isUsdc && !isUsd ? 'none' : undefined,
            },
            iconStyles: {
              display: isUsdc && !isUsd ? 'none' : undefined,
            },
            wrapperStyles: {
              color: isFinished ? (isWinningColor ? 'brand.green' : 'brand.error') : 'white',
              fontSize: 'sm',
            },
            iconSize: '18px',
            denom: denom,
          }}
          value={displayValue}
        />
        <Text fontSize={'sm'} color={'ninja.300'} fontWeight={700}>
          / {maxPoolDisplay}
        </Text>
      </Flex>
    </Flex>
  )
}

const BetsList = ({ type }: { type: DoubleColor }) => {
  const bets = useStoreWithEqualityFn(
    useDoubleStore,
    (state) => state.bets[type],
    (oldBets, newBets) => JSON.stringify(oldBets) == JSON.stringify(newBets)
  )

  return bets.length ? (
    <Box px={3} display={{ base: 'none', md: 'flex' }}>
      <List w={'full'}>
        {bets.map((bet, index) => {
          return <UserBet key={index} address={bet.player} amount={bet.bet} denom={bet.denom.replaceAll('_', '/')} />
        })}
      </List>
    </Box>
  ) : null
}

// const PlayerBet = ({ type }: { type: DoubleColor }) => {
//   const { account } = useSession()
//   const { bets, denom, currentRoll, isRoundFinished } = useDoubleStore(
//     ({ bets, denom, currentRoll, isRoundFinished }) => ({ bets, denom, currentRoll, isRoundFinished })
//   )

//   const playerBet = (() => {
//     if (!!account) {
//       const amount = bets[type]
//         .filter((bet) => bet.player === account.injectiveAddress && bet.denom.replaceAll('_', '/') === denom)
//         .reduce((prev, curr) => prev.plus(curr.bet), new BigNumberInWei(0))
//         .toString()

//       if (amount === '0') {
//         return null
//       }

//       return new TokenMoney(amount, denom).humanCompactFormat
//     }

//     return null
//   })()

//   const isWinningColor = getDoubleWinningColor(currentRoll) === type

//   return playerBet !== null ? (
//     <Flex
//       display={{ base: 'none', md: 'flex' }}
//       mb={3}
//       bg={'ninja.700'}
//       rounded={'xl'}
//       _last={{
//         mb: 0,
//       }}
//       gap={3}
//       h={'45px'}
//       px={3}
//       justifyContent={'space-between'}
//       alignItems={'center'}
//     >
//       <Text fontWeight={700} fontSize={'sm'}>
//         Your bet
//       </Text>
//       <Amount
//         wrapperStyles={{
//           fontSize: 'sm',
//         }}
//         currencyStyles={{
//           display: 'none',
//         }}
//         // textColor={isRoundFinished ? (isWinningColor ? 'brand.green' : 'brand.error') : 'white'}
//         value={playerBet}
//         denom={denom.replaceAll('_', '/')}
//       />
//     </Flex>
//   ) : null
// }

const buttonsColors: Record<DoubleColor, string> = {
  [DoubleColor.Black]: `linear-gradient(292deg, #8E83FB 31.11%, #5D52EE 97.01%);`,
  [DoubleColor.Red]: `linear-gradient(99deg, #E383FB -4.78%, #5D52EE 102.77%);`,
  [DoubleColor.Green]: `linear-gradient(99deg, #73DE31 -54.11%, #0BB9F0 101.79%), linear-gradient(93deg, #24C4DA 4.34%, #5F95FF 99.76%);`,
}

const buttonsHoverColors: Record<DoubleColor, string> = {
  [DoubleColor.Black]: `linear-gradient(#645AD6, #645AD6);`,
  [DoubleColor.Red]: `linear-gradient(#A26BF5, #A26BF5)`,
  [DoubleColor.Green]: `linear-gradient(#30B28F, #30B28F);`,
}

const buttonsHoverShadow: Record<DoubleColor, string> = {
  [DoubleColor.Black]: `0px 2px 48px -4px #645AD6CC;`,
  [DoubleColor.Red]: `0px 2px 48px -4px #645AD6CC;`,
  [DoubleColor.Green]: `0px 2px 48px -4px #30B28FCC;`,
}
